import { useRef, useEffect, useState } from 'react'
import { useThree, useLoader, useFrame } from '@react-three/fiber'
import { meshBounds, SpotLight, useHelper, useGLTF } from '@react-three/drei'
import { SpotLightHelper, Object3D, AudioLoader, AudioListener, Audio } from 'three'
import { useControls } from 'leva'

export default function DeskLamp()
{
    const model = useGLTF('./models/electro/desk_lamp_arm_01/desk_lamp_arm_01_1k.gltf')

    // traverse model
    useEffect(() => {
        model.scene.traverse((child) => {
            if (child.isMesh) {
                child.castShadow = true // Shadow
                child.raycast = meshBounds // Apply meshBounds to each mesh
            }
        })
    }, [model])

    /**
     * Leva
     */
    
    const leva = useControls('desk lamp', 
    {
        // positions
        lampX:{value:0.9,min:-1.1,max:1.13},
        lampY:{value:-1.04,min:-1.5,max:0},
        lampZ:{value:-0.55,min:-0.6,max:-0.3},
        
        // spot light settings
        color:'#ff7300',
        angle:{value:0.98,min:0,max:2},
        attenuation:{value:3.06,min:0,max:6},
        anglePower:{value:2.98,min:0,max:6},
        intensity:{value:1,min:0,max:3},
        penumbra:{value:1,min:0,max:2},
        decay:{value:2,min:0,max:6}
    },
    { collapsed: true }
    )

    /**
     * Lights
     */

    const lightRef = useRef()
    const ambientLightRef = useRef()

    // Helper
    // useHelper(lightRef, SpotLightHelper, 1) 

    // Intensity
    useFrame( (state,delta) => {
        lightRef.current.intensity =  (
            10 +
            Math.random() * 7 +
            Math.sin(state.clock.elapsedTime) * 5
            ) * leva.intensity
    } )

    // Target
    const targetRef = useRef(new Object3D())
    const { scene } = useThree()
       

    // Add target
    useEffect(() => {
        scene.add(targetRef.current)
        lightRef.current.target = targetRef.current
    }, [scene])

    // Position the target
    useEffect(() => {
        targetRef.current.position.set(leva.lampX-0.32, leva.lampY-0.96, leva.lampZ+0.91); // Adjust target position as needed
    }, [ leva.lampX,leva.lampY, leva.lampZ ])

    /**
     * Sounds
     */

    const { camera } = useThree()
    
    // Load and decode button click sound
    const buttonClickSound = useLoader(AudioLoader, './sounds/light-switch-81967.mp3')
    // Load and decode lights flicker on and some electrical noises
    const lightsFlickerSound = useLoader(AudioLoader, './sounds/lights-flicker-on-and-some-electrical-noises-33079.mp3')    


    const [lightOn, setLightOn] = useState(true); // State to track TV status
    const lightsFlickerRef = useRef(); // Ref to manage white noise sound


    const playButtonClickSound = () => {
        // Create a new AudioListener and Audio instance for each play action
        const listener = new AudioListener()
        camera.add(listener) // Attach the listener to the camera

        const sound = new Audio(listener) // Audio is a player
        sound.setBuffer(buttonClickSound) // assign decoded audio to Audio player
        sound.setVolume(0.5)
        sound.play()

        sound.onEnded = () => {
            camera.remove(listener) // Clean up the listener once the sound has played
        }
    }

    // Initialize and configure lights flicker sound
    useEffect(() => {
        const listener = new AudioListener();
        camera.add(listener);

        const sound = new Audio(listener);
        sound.setBuffer(lightsFlickerSound);
        sound.setLoop(true); // Loop the white noise
        sound.setVolume(1);

        lightsFlickerRef.current = sound;

        return () => {
            camera.remove(listener); // Cleanup
        };
    }, [camera, lightsFlickerSound]);

    /**
     * Clicker
     */
   
    const eventHandler = (event) => 
    {
        lightRef.current.visible = !lightRef.current.visible
        ambientLightRef.current.visible = !ambientLightRef.current.visible

        event.stopPropagation()

        playButtonClickSound()

        setLightOn(!lightOn); // Toggle light state
        // Toggle flicker sound based on TV state
        if (!lightOn) {
            lightsFlickerRef.current?.play();
        } else {
            lightsFlickerRef.current?.pause();
        }
    }  

    return <>
    
        {/* Model */}
        <primitive
            object={model.scene}
            position={[ leva.lampX, leva.lampY, leva.lampZ ]}
            rotation-y={2.5}
            raycast={ meshBounds }
            onClick={ eventHandler } 
            onPointerEnter={ () => { document.body.style.cursor = 'pointer' } }
            onPointerLeave={ () => { document.body.style.cursor = 'default' } }
        />

        {/* Light */}
        <SpotLight 
            ref={lightRef}
            castShadow 
            position={[leva.lampX-0.1, leva.lampY+0.64, leva.lampZ+0.17]} 
            angle={leva.angle}
            attenuation={leva.attenuation}
            anglePower={leva.anglePower}
            intensity={10}
            penumbra={leva.penumbra}
            color={leva.color}
            decay={leva.decay}
            shadow-normalBias={ 0.01 }
        />

        {/* Ambient Light */}
        <ambientLight 
            ref={ ambientLightRef }
            intensity={ 0.1 * leva.intensity } 
            color={leva.color}
        />
    
    </>
}